// Phygital · feature flag hook + admin override support.
// Server flags at /api/phygital/flags. Admin can force-override any flag
// client-side via localStorage.phygital_flag_overrides (JSON map).

import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OVERRIDE_KEY = "phygital_flag_overrides";
const VIEW_AS_COHORT_KEY = "phygital_view_as_cohort";

let cachedFlags = null;
let cachedFor = null;
const listeners = new Set();

function authHeader() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function getOverrides() {
  try { return JSON.parse(localStorage.getItem(OVERRIDE_KEY) || "{}"); }
  catch { return {}; }
}

export function setOverride(flagId, value) {
  const cur = getOverrides();
  if (value === null || value === undefined) delete cur[flagId];
  else cur[flagId] = !!value;
  localStorage.setItem(OVERRIDE_KEY, JSON.stringify(cur));
  listeners.forEach((fn) => fn());
}

export function clearOverrides() {
  localStorage.removeItem(OVERRIDE_KEY);
  listeners.forEach((fn) => fn());
}

export function getViewAsCohort() {
  return localStorage.getItem(VIEW_AS_COHORT_KEY) || null;
}

export function setViewAsCohort(cohortId) {
  if (!cohortId) localStorage.removeItem(VIEW_AS_COHORT_KEY);
  else localStorage.setItem(VIEW_AS_COHORT_KEY, cohortId);
  cachedFlags = null; cachedFor = null;
  listeners.forEach((fn) => fn());
}

async function loadFlags() {
  const token = localStorage.getItem("session_token") || "anon";
  const viewAs = getViewAsCohort();
  const cacheKey = `${token}::${viewAs || ""}`;
  if (cachedFlags && cachedFor === cacheKey) return cachedFlags;
  const url = token && token !== "anon" ? `${API}/phygital/flags` : `${API}/phygital/flags/public`;
  try {
    const r = await axios.get(url, { headers: authHeader() });
    cachedFlags = r.data;
    cachedFor = cacheKey;
    return r.data;
  } catch {
    cachedFlags = { flags: {}, cohorts: ["all"] };
    cachedFor = cacheKey;
    return cachedFlags;
  }
}

function applyOverrides(flags) {
  const overrides = getOverrides();
  return { ...flags, ...overrides };
}

export function usePhygitalFlags() {
  const start = cachedFlags ? applyOverrides(cachedFlags.flags) : {};
  const [state, setState] = useState({ flags: start, cohorts: cachedFlags?.cohorts || [], loading: !cachedFlags });
  useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      const d = await loadFlags();
      if (!mounted) return;
      setState({ flags: applyOverrides(d.flags || {}), cohorts: d.cohorts || [], loading: false });
    };
    refresh();
    const listener = () => {
      if (cachedFlags) setState({ flags: applyOverrides(cachedFlags.flags || {}), cohorts: cachedFlags.cohorts || [], loading: false });
      else refresh();
    };
    listeners.add(listener);
    return () => { mounted = false; listeners.delete(listener); };
  }, []);
  return state;
}

export function useFlag(flagId) {
  const { flags } = usePhygitalFlags();
  return !!flags[flagId];
}

export function refreshFlags() {
  cachedFlags = null;
  cachedFor = null;
  listeners.forEach((fn) => fn());
}
